import { CLASS_DEFS, ChampionClass, type ClassDef, type GameMode } from "@craftgaming/shared";

export type MainMenuResult = {
  selectedClass: ClassDef;
  playerName: string;
  gameMode: GameMode;
};

export function showMainMenu(): Promise<MainMenuResult> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.id = "main-menu";
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 50%, #0d1b2a 100%);
      z-index: 200;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-family: 'Segoe UI', system-ui, sans-serif;
      color: #e0e0e0;
      overflow-y: auto;
      padding: 20px;
    `;

    // Logo
    const logo = document.createElement("h1");
    logo.textContent = "CraftGaming AI";
    logo.style.cssText = `
      font-size: 3.5rem;
      font-weight: 800;
      margin-bottom: 0.5rem;
      background: linear-gradient(135deg, #4ade80, #22d3ee, #a78bfa);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      text-shadow: none;
      letter-spacing: -1px;
    `;
    overlay.appendChild(logo);

    // Subtitle
    const subtitle = document.createElement("p");
    subtitle.textContent = "Sobreviva. Construa. Conquiste.";
    subtitle.style.cssText = `
      font-size: 1.1rem;
      color: #888;
      margin-bottom: 2rem;
      font-style: italic;
    `;
    overlay.appendChild(subtitle);

    // Name input
    const nameSection = document.createElement("div");
    nameSection.style.cssText = `margin-bottom: 1.5rem; text-align: center;`;

    const nameLabel = document.createElement("label");
    nameLabel.textContent = "Seu nome";
    nameLabel.style.cssText = `display: block; font-size: 0.85rem; color: #aaa; margin-bottom: 6px;`;
    nameSection.appendChild(nameLabel);

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.placeholder = "Digite seu nome...";
    nameInput.maxLength = 16;
    nameInput.value = localStorage.getItem("craftgaming_name") || "";
    nameInput.style.cssText = `
      width: 220px;
      padding: 10px 14px;
      border: 2px solid #333;
      border-radius: 8px;
      background: #111;
      color: #fff;
      font-size: 1rem;
      text-align: center;
      outline: none;
      transition: border-color 0.2s;
    `;
    nameInput.addEventListener("focus", () => { nameInput.style.borderColor = "#4ade80"; });
    nameInput.addEventListener("blur", () => { nameInput.style.borderColor = "#333"; });
    nameSection.appendChild(nameInput);
    overlay.appendChild(nameSection);

    // Class selection header
    const classHeader = document.createElement("h2");
    classHeader.textContent = "Escolha sua classe";
    classHeader.style.cssText = `
      font-size: 1.3rem;
      font-weight: 600;
      margin-bottom: 1rem;
      color: #ccc;
    `;
    overlay.appendChild(classHeader);

    // Class grid
    const classGrid = document.createElement("div");
    classGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
      max-width: 960px;
      width: 100%;
      margin-bottom: 2rem;
    `;

    let selectedClass: ChampionClass = ChampionClass.WARRIOR;
    const classCards: HTMLDivElement[] = [];

    for (const [classId, classDef] of Object.entries(CLASS_DEFS)) {
      const card = document.createElement("div");
      const color = classDef.color;
      card.style.cssText = `
        padding: 16px;
        border: 2px solid #333;
        border-radius: 12px;
        background: #111;
        cursor: pointer;
        transition: all 0.2s;
        text-align: center;
      `;

      card.innerHTML = `
        <div style="width:48px;height:48px;border-radius:50%;background:radial-gradient(circle,${color},${color}44);margin:0 auto 8px;"></div>
        <h3 style="font-size:1.1rem;margin:0 0 4px;color:${color};">${classDef.name}</h3>
        <p style="font-size:0.75rem;color:#888;margin:0 0 8px;font-style:italic;">${classDef.title}</p>
        <div style="font-size:0.7rem;color:#666;line-height:1.4;">
          <span>HP: ${classDef.maxHealth}</span> · <span>MP: ${classDef.maxMana}</span>
        </div>
        <div style="font-size:0.65rem;color:#555;margin-top:6px;">
          ${classDef.abilities.map((a) => a.name).join(" · ")}
        </div>
      `;

      card.addEventListener("click", () => {
        selectedClass = classId as ChampionClass;
        updateClassSelection();
      });

      card.addEventListener("mouseenter", () => {
        if (classId !== selectedClass) {
          card.style.borderColor = "#555";
          card.style.transform = "translateY(-2px)";
        }
      });
      card.addEventListener("mouseleave", () => {
        if (classId !== selectedClass) {
          card.style.borderColor = "#333";
          card.style.transform = "none";
        }
      });

      classGrid.appendChild(card);
      classCards.push(card);
    }
    overlay.appendChild(classGrid);

    function updateClassSelection() {
      const classes = Object.keys(CLASS_DEFS) as ChampionClass[];
      for (let i = 0; i < classCards.length; i++) {
        const color = CLASS_DEFS[classes[i]].color;
        if (classes[i] === selectedClass) {
          classCards[i].style.borderColor = color;
          classCards[i].style.background = `${color}11`;
          classCards[i].style.transform = "translateY(-2px)";
        } else {
          classCards[i].style.borderColor = "#333";
          classCards[i].style.background = "#111";
          classCards[i].style.transform = "none";
        }
      }
    }
    updateClassSelection();

    // ─── Game mode selection ───
    const modeHeader = document.createElement("h2");
    modeHeader.textContent = "Modo de Jogo";
    modeHeader.style.cssText = `font-size: 1.1rem; font-weight: 600; margin-bottom: 0.8rem; color: #ccc;`;
    overlay.appendChild(modeHeader);

    let selectedMode: GameMode = "survival";
    const modeRow = document.createElement("div");
    modeRow.style.cssText = `display: flex; gap: 12px; margin-bottom: 2rem;`;

    const modeDefs: Array<{ id: GameMode; label: string; desc: string; color: string }> = [
      { id: "survival", label: "⚔️ Sobrevivência", desc: "Vida, fome, mobs e queda", color: "#4ade80" },
      { id: "creative", label: "✨ Criativo", desc: "Voar (2× espaço), blocos infinitos, invencível", color: "#22d3ee" },
    ];

    const modeCards: HTMLDivElement[] = [];
    for (const mode of modeDefs) {
      const card = document.createElement("div");
      card.style.cssText = `
        padding: 12px 18px;
        border: 2px solid #333;
        border-radius: 10px;
        background: #111;
        cursor: pointer;
        text-align: center;
        transition: all 0.2s;
        min-width: 190px;
      `;
      card.innerHTML = `
        <div style="font-size:1rem;font-weight:700;color:${mode.color};margin-bottom:4px;">${mode.label}</div>
        <div style="font-size:0.7rem;color:#888;">${mode.desc}</div>
      `;
      card.addEventListener("click", () => {
        selectedMode = mode.id;
        updateModeSelection();
      });
      modeRow.appendChild(card);
      modeCards.push(card);
    }

    function updateModeSelection() {
      for (let i = 0; i < modeCards.length; i++) {
        const isSelected = modeDefs[i].id === selectedMode;
        modeCards[i].style.borderColor = isSelected ? modeDefs[i].color : "#333";
        modeCards[i].style.background = isSelected ? `${modeDefs[i].color}11` : "#111";
      }
    }
    updateModeSelection();
    overlay.appendChild(modeRow);

    // Play button
    const playBtn = document.createElement("button");
    playBtn.textContent = "ENTRAR NO MUNDO";
    playBtn.style.cssText = `
      padding: 14px 48px;
      font-size: 1.1rem;
      font-weight: 700;
      border: none;
      border-radius: 10px;
      background: linear-gradient(135deg, #4ade80, #22d3ee);
      color: #000;
      cursor: pointer;
      transition: all 0.2s;
      letter-spacing: 0.5px;
    `;
    playBtn.addEventListener("mouseenter", () => {
      playBtn.style.transform = "scale(1.05)";
      playBtn.style.boxShadow = "0 4px 20px rgba(74,222,128,0.3)";
    });
    playBtn.addEventListener("mouseleave", () => {
      playBtn.style.transform = "scale(1)";
      playBtn.style.boxShadow = "none";
    });
    playBtn.addEventListener("click", () => {
      const name = nameInput.value.trim() || "Player";
      localStorage.setItem("craftgaming_name", name);
      overlay.style.opacity = "0";
      overlay.style.transition = "opacity 0.5s";
      setTimeout(() => {
        overlay.remove();
        resolve({
          selectedClass: CLASS_DEFS[selectedClass],
          playerName: name,
          gameMode: selectedMode,
        });
      }, 500);
    });
    overlay.appendChild(playBtn);

    // Footer
    const footer = document.createElement("p");
    footer.textContent = "2V Educação — CraftGaming AI v0.1";
    footer.style.cssText = `
      position: absolute;
      bottom: 16px;
      font-size: 0.7rem;
      color: #444;
    `;
    overlay.appendChild(footer);

    document.body.appendChild(overlay);
    nameInput.focus();
  });
}
